import * as XLSX from 'xlsx';
import camelCase from 'lodash.camelcase';
import geocode from '../utils/geocodeHelper';

const formatItem = (item, geocodedPlaces) => {
  const formattedItem = { ...item };

  try {
    for (const key of Object.keys(item)) {
      if (key.endsWith('Type') && !!item[key]) {
        const parentKey = `${key.replace(/\d+Type$/, '')}s`;
        const valueKey = key.replace('Type', 'Value');

        if (!formattedItem[parentKey]) {
          formattedItem[parentKey] = {};
        }
        formattedItem[parentKey][item[key]] = {
          [parentKey === 'loadDemands' ? 'amount' : 'maxLoad']: Math.round(item[valueKey]),
        };

        delete formattedItem[key];
        delete formattedItem[valueKey];
      } else if (key.endsWith('Time') && !!item[key]) {
        formattedItem[key] = item[key].toString().trim();
      } else if (key.endsWith('Waypoint')) {
        const waypointData = geocodedPlaces[`${item.label}-${key}`];
        if (waypointData && waypointData.latitude) {
          formattedItem[`${key}Latitude`] = waypointData.latitude;
          formattedItem[`${key}Longitude`] = waypointData.longitude;
        }
      }
    }
  } catch (error) {
    console.error(`Error formatting item: ${item}`, error);
    throw new Error(`Error formatting item: ${error.message}`);
  }

  return formattedItem;
};

const convertDataFormat = async (data) => {
  try {
    const filteredItems = data.filter((item) => item.label);
    const placesData = {};

    // Collect all places that need to be geocoded
    filteredItems.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key.endsWith('Waypoint') && item[key]) {
          const latitudeKey = `${key}Latitude`;
          const longitudeKey = `${key}Longitude`;
          if (!item[latitudeKey] || !item[longitudeKey]) {
            placesData[`${item.label}-${key}`] = { address: item[key] };
          }
        }
      });
    });

    let geocodedPlaces = {};

    // Call the geocode function once with all the collected places
    if (Object.keys(placesData).length) {
      // Only require API key when there are addresses that need geocoding
      const storedKey = localStorage.getItem('google_geocode_key');
      const googleKey = storedKey || process.env.REACT_APP_GEOCODE_KEY;
      if (!googleKey) {
        throw new Error(
          'Some rows have blank latitude/longitude fields and require geocoding. Please enter and save your Google Geocoding API key first.',
        );
      }
      geocodedPlaces = await geocode(placesData);
    }

    // Format each item using the geocoded data
    const formattedItems = filteredItems.map((item) => formatItem(item, geocodedPlaces));

    return formattedItems;
  } catch (error) {
    console.error('Error converting data format:', error);
    throw new Error(`Error converting data format: ${error.message}`);
  }
};

const convertToObjects = (data) => {
  try {
    const headers = data[0];
    return data
      .slice(1)
      .filter((row) => row.some((cell) => cell !== null && cell !== undefined && cell !== '')) // Ignore empty rows
      .map((row) => {
        return headers.reduce((acc, header, index) => {
          const camelCaseKey = camelCase(header);
          acc[camelCaseKey] = row[index];
          return acc;
        }, {});
      });
  } catch (error) {
    console.error('Error converting to objects:', error);
    throw new Error(`Error converting to objects: ${error.message}`);
  }
};

export const handleFileRead = (file, setData, onError) => {
  if (!file) {
    setData(null);
    return;
  }

  const reader = new FileReader();

  reader.onload = async (e) => {
    try {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      // Handle the asynchronous operation here
      const objects = convertToObjects(json);
      const camelCasedData = await convertDataFormat(objects); // Await the asynchronous conversion
      setData(camelCasedData);
    } catch (error) {
      console.error('Error reading and processing Excel file:', error);
      if (onError) {
        onError(error.message);
      }
      setData(null); // Reset data in case of error
    }
  };

  reader.onerror = (error) => {
    console.error('Error reading file:', error);
    if (onError) {
      onError('Error reading file');
    }
    setData(null); // Reset data in case of error
  };

  reader.readAsArrayBuffer(file);
};
